'use client'

import * as React from 'react'

import type { SharedContent } from '../lib/content/types'
import { DownloadCta } from './download-cta'

export function DownloadBar({ shared }: { shared: SharedContent }) {
  const sentinelRef = React.useRef<HTMLDivElement>(null)
  const [stuck, setStuck] = React.useState(false)

  React.useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) {
      return
    }

    // Studio scrolls an inner pane; standalone hosts can scroll the document.
    let root = sentinel.parentElement
    while (root && !/(auto|scroll)/.test(getComputedStyle(root).overflowY)) {
      root = root.parentElement
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        setStuck(
          !entry.isIntersecting && entry.boundingClientRect.top < (entry.rootBounds?.top ?? 0),
        )
      },
      { root },
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [])

  return (
    <>
      <section
        aria-label={shared.nav.download}
        aria-hidden={stuck}
        inert={stuck}
        className="px-3 py-4"
      >
        <DownloadCta {...shared.download} />
      </section>
      <div ref={sentinelRef} className="h-px" aria-hidden="true" />
      {/* A zero-height sticky layer keeps the page geometry stable as downloads appear. */}
      <div className="sticky top-0 z-[1] h-0">
        <section
          aria-label={shared.nav.download}
          aria-hidden={!stuck}
          className={
            stuck
              ? 'visible border-b border-sd-border bg-sd-surface px-3 py-3'
              : 'invisible px-3 py-3'
          }
        >
          <DownloadCta {...shared.download} compact />
        </section>
      </div>
    </>
  )
}
